'use client';

import { useState } from 'react';

type InsuranceType = 'auto' | 'home' | 'life';

interface AutoInsuranceState {
  age: number;
  vehicleValue: number;
  drivingRecord: 'excellent' | 'good' | 'average' | 'poor';
  coverageLevel: 'basic' | 'standard' | 'premium';
}

interface HomeInsuranceState {
  homeValue: number;
  deductible: number;
  coverageLevel: 'basic' | 'standard' | 'premium';
}

interface LifeInsuranceState {
  age: number;
  coverageAmount: number;
  term: number;
  healthStatus: 'excellent' | 'good' | 'average' | 'poor';
}

const INITIAL_AUTO: AutoInsuranceState = {
  age: 35,
  vehicleValue: 25000,
  drivingRecord: 'good',
  coverageLevel: 'standard',
};

const INITIAL_HOME: HomeInsuranceState = {
  homeValue: 300000,
  deductible: 1000,
  coverageLevel: 'standard',
};

const INITIAL_LIFE: LifeInsuranceState = {
  age: 35,
  coverageAmount: 500000,
  term: 20,
  healthStatus: 'good',
};

const AUTO_PRESETS = {
  conservative: {
    age: 45,
    vehicleValue: 15000,
    drivingRecord: 'excellent' as const,
    coverageLevel: 'basic' as const,
  },
  moderate: {
    age: 35,
    vehicleValue: 25000,
    drivingRecord: 'good' as const,
    coverageLevel: 'standard' as const,
  },
  aggressive: {
    age: 22,
    vehicleValue: 45000,
    drivingRecord: 'average' as const,
    coverageLevel: 'premium' as const,
  },
};

const HOME_PRESETS = {
  conservative: {
    homeValue: 200000,
    deductible: 2000,
    coverageLevel: 'basic' as const,
  },
  moderate: {
    homeValue: 300000,
    deductible: 1000,
    coverageLevel: 'standard' as const,
  },
  aggressive: {
    homeValue: 500000,
    deductible: 500,
    coverageLevel: 'premium' as const,
  },
};

const LIFE_PRESETS = {
  conservative: {
    age: 50,
    coverageAmount: 250000,
    term: 10,
    healthStatus: 'good' as const,
  },
  moderate: {
    age: 35,
    coverageAmount: 500000,
    term: 20,
    healthStatus: 'good' as const,
  },
  aggressive: {
    age: 25,
    coverageAmount: 1000000,
    term: 30,
    healthStatus: 'excellent' as const,
  },
};

export default function CalculatorInsurance() {
  const [insuranceType, setInsuranceType] = useState<InsuranceType>('auto');
  
  const [autoInputs, setAutoInputs] = useState<AutoInsuranceState>(INITIAL_AUTO);

  const [homeInputs, setHomeInputs] = useState<HomeInsuranceState>(INITIAL_HOME);

  const [lifeInputs, setLifeInputs] = useState<LifeInsuranceState>(INITIAL_LIFE);

  const calculateAutoPremium = () => {
    let basePremium = autoInputs.vehicleValue * 0.04;
    
    // Age factor
    if (autoInputs.age < 25) basePremium *= 1.5;
    else if (autoInputs.age > 65) basePremium *= 1.2;
    else if (autoInputs.age >= 30 && autoInputs.age <= 60) basePremium *= 0.9;
    
    // Driving record factor
    const recordMultiplier = {
      excellent: 0.8,
      good: 1.0,
      average: 1.3,
      poor: 1.8,
    };
    basePremium *= recordMultiplier[autoInputs.drivingRecord];
    
    // Coverage level factor
    const coverageMultiplier = {
      basic: 0.7,
      standard: 1.0,
      premium: 1.4,
    };
    basePremium *= coverageMultiplier[autoInputs.coverageLevel];
    
    const monthlyPremium = basePremium / 12;
    const annualPremium = basePremium;
    
    return { monthlyPremium, annualPremium };
  };

  const calculateHomePremium = () => {
    let basePremium = homeInputs.homeValue * 0.0035;
    
    // Deductible factor (higher deductible = lower premium)
    const deductibleFactor = 1 - (homeInputs.deductible / 10000) * 0.15;
    basePremium *= Math.max(deductibleFactor, 0.85);
    
    // Coverage level factor
    const coverageMultiplier = {
      basic: 0.8,
      standard: 1.0,
      premium: 1.3,
    };
    basePremium *= coverageMultiplier[homeInputs.coverageLevel];
    
    const monthlyPremium = basePremium / 12;
    const annualPremium = basePremium;
    
    return { monthlyPremium, annualPremium };
  };

  const calculateLifePremium = () => {
    let basePremium = (lifeInputs.coverageAmount / 1000) * 0.5;
    
    // Age factor
    const ageFactor = 1 + ((lifeInputs.age - 25) * 0.03);
    basePremium *= Math.max(ageFactor, 0.5);
    
    // Health status factor
    const healthMultiplier = {
      excellent: 0.8,
      good: 1.0,
      average: 1.3,
      poor: 1.8,
    };
    basePremium *= healthMultiplier[lifeInputs.healthStatus];
    
    // Term factor (longer term = slightly higher premium)
    const termFactor = 1 + (lifeInputs.term / 100);
    basePremium *= termFactor;
    
    const monthlyPremium = basePremium;
    const annualPremium = basePremium * 12;
    const totalPaid = annualPremium * lifeInputs.term;
    
    return { monthlyPremium, annualPremium, totalPaid };
  };

  const autoResults = calculateAutoPremium();
  const homeResults = calculateHomePremium();
  const lifeResults = calculateLifePremium();

  const handleReset = () => {
    if (insuranceType === 'auto') {
      setAutoInputs(INITIAL_AUTO);
    } else if (insuranceType === 'home') {
      setHomeInputs(INITIAL_HOME);
    } else if (insuranceType === 'life') {
      setLifeInputs(INITIAL_LIFE);
    }
  };

  const handlePreset = (preset: 'conservative' | 'moderate' | 'aggressive') => {
    if (insuranceType === 'auto') {
      setAutoInputs(AUTO_PRESETS[preset]);
    } else if (insuranceType === 'home') {
      setHomeInputs(HOME_PRESETS[preset]);
    } else if (insuranceType === 'life') {
      setLifeInputs(LIFE_PRESETS[preset]);
    }
  };

  return (
    <div className="space-y-8">
      {/* Insurance Type Selector */}
      <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-6">Select Insurance Type</h2>
        <div className="grid md:grid-cols-3 gap-4">
          <button
            onClick={() => setInsuranceType('auto')}
            className={`p-6 rounded-2xl border-2 transition-all ${
              insuranceType === 'auto' ?'border-blue-500 bg-blue-50 shadow-lg' :'border-slate-200 hover:border-blue-300'
            }`}
          >
            <div className="text-4xl mb-3">🚗</div>
            <div className="font-bold text-slate-900">Auto Insurance</div>
            <div className="text-sm text-slate-600 mt-1">Vehicle coverage</div>
          </button>
          <button
            onClick={() => setInsuranceType('home')}
            className={`p-6 rounded-2xl border-2 transition-all ${
              insuranceType === 'home' ?'border-emerald-500 bg-emerald-50 shadow-lg' :'border-slate-200 hover:border-emerald-300'
            }`}
          >
            <div className="text-4xl mb-3">🏠</div>
            <div className="font-bold text-slate-900">Home Insurance</div>
            <div className="text-sm text-slate-600 mt-1">Property protection</div>
          </button>
          <button
            onClick={() => setInsuranceType('life')}
            className={`p-6 rounded-2xl border-2 transition-all ${
              insuranceType === 'life'
                ? 'border-purple-500 bg-purple-50 shadow-lg' :'border-slate-200 hover:border-purple-300'
            }`}
          >
            <div className="text-4xl mb-3">❤️</div>
            <div className="font-bold text-slate-900">Life Insurance</div>
            <div className="text-sm text-slate-600 mt-1">Family security</div>
          </button>
        </div>
      </div>

      {/* Auto Insurance Calculator */}
      {insuranceType === 'auto' && (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-gradient-to-b from-blue-500 to-indigo-600 rounded-full"></div>
                <h2 className="text-2xl font-bold text-slate-900">Auto Insurance Details</h2>
              </div>

              {/* Preset and Reset Buttons */}
              <div className="mb-6 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handlePreset('conservative')}
                    className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
                  >
                    Conservative
                  </button>
                  <button
                    onClick={() => handlePreset('moderate')}
                    className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
                  >
                    Moderate
                  </button>
                  <button
                    onClick={() => handlePreset('aggressive')}
                    className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
                  >
                    Aggressive
                  </button>
                </div>
                <button
                  onClick={handleReset}
                  className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
                >
                  Reset to Default
                </button>
              </div>

              <div className="space-y-6">
                <InputField
                  label="Your Age"
                  value={autoInputs.age}
                  onChange={(v) => setAutoInputs({ ...autoInputs, age: v })}
                  min={16}
                  max={100}
                  unit="years"
                />
                <InputField
                  label="Vehicle Value"
                  value={autoInputs.vehicleValue}
                  onChange={(v) => setAutoInputs({ ...autoInputs, vehicleValue: v })}
                  min={5000}
                  prefix="$"
                />
                <SelectField
                  label="Driving Record"
                  value={autoInputs.drivingRecord}
                  onChange={(v) => setAutoInputs({ ...autoInputs, drivingRecord: v as any })}
                  options={[
                    { value: 'excellent', label: 'Excellent (No violations)' },
                    { value: 'good', label: 'Good (1 minor violation)' },
                    { value: 'average', label: 'Average (2-3 violations)' },
                    { value: 'poor', label: 'Poor (4+ violations)' },
                  ]}
                />
                <SelectField
                  label="Coverage Level"
                  value={autoInputs.coverageLevel}
                  onChange={(v) => setAutoInputs({ ...autoInputs, coverageLevel: v as any })}
                  options={[
                    { value: 'basic', label: 'Basic (Liability only)' },
                    { value: 'standard', label: 'Standard (Liability + Collision)' },
                    { value: 'premium', label: 'Premium (Full coverage)' },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-600 rounded-3xl p-10 shadow-modern-xl text-white">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
                Estimated Monthly Premium
              </div>
              <div className="text-6xl font-bold mb-2">
                ${autoResults.monthlyPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-lg opacity-90">Per month</div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <ResultCard
                label="Annual Premium"
                value={`$${autoResults.annualPremium.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                description="Total yearly cost"
                gradient="from-blue-500 to-indigo-600"
              />
              <ResultCard
                label="Coverage Level"
                value={autoInputs.coverageLevel.charAt(0).toUpperCase() + autoInputs.coverageLevel.slice(1)}
                description="Selected protection"
                gradient="from-emerald-500 to-teal-600"
              />
            </div>
          </div>
        </div>
      )}

      {/* Home Insurance Calculator */}
      {insuranceType === 'home' && (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-gradient-to-b from-emerald-500 to-teal-600 rounded-full"></div>
                <h2 className="text-2xl font-bold text-slate-900">Home Insurance Details</h2>
              </div>

              {/* Preset and Reset Buttons */}
              <div className="mb-6 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handlePreset('conservative')}
                    className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
                  >
                    Conservative
                  </button>
                  <button
                    onClick={() => handlePreset('moderate')}
                    className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
                  >
                    Moderate
                  </button>
                  <button
                    onClick={() => handlePreset('aggressive')}
                    className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
                  >
                    Aggressive
                  </button>
                </div>
                <button
                  onClick={handleReset}
                  className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
                >
                  Reset to Default
                </button>
              </div>

              <div className="space-y-6">
                <InputField
                  label="Home Value"
                  value={homeInputs.homeValue}
                  onChange={(v) => setHomeInputs({ ...homeInputs, homeValue: v })}
                  min={50000}
                  prefix="$"
                />
                <InputField
                  label="Deductible"
                  value={homeInputs.deductible}
                  onChange={(v) => setHomeInputs({ ...homeInputs, deductible: v })}
                  min={500}
                  max={10000}
                  step={500}
                  prefix="$"
                />
                <SelectField
                  label="Coverage Level"
                  value={homeInputs.coverageLevel}
                  onChange={(v) => setHomeInputs({ ...homeInputs, coverageLevel: v as any })}
                  options={[
                    { value: 'basic', label: 'Basic (Dwelling only)' },
                    { value: 'standard', label: 'Standard (Dwelling + Contents)' },
                    { value: 'premium', label: 'Premium (Full replacement)' },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 rounded-3xl p-10 shadow-modern-xl text-white">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
                Estimated Monthly Premium
              </div>
              <div className="text-6xl font-bold mb-2">
                ${homeResults.monthlyPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-lg opacity-90">Per month</div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <ResultCard
                label="Annual Premium"
                value={`$${homeResults.annualPremium.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                description="Total yearly cost"
                gradient="from-emerald-500 to-teal-600"
              />
              <ResultCard
                label="Deductible"
                value={`$${homeInputs.deductible.toLocaleString('en-US')}`}
                description="Out-of-pocket per claim"
                gradient="from-blue-500 to-indigo-600"
              />
            </div>
          </div>
        </div>
      )}

      {/* Life Insurance Calculator */}
      {insuranceType === 'life' && (
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-8 shadow-modern-xl border border-slate-200 sticky top-24">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-2 h-8 bg-gradient-to-b from-purple-500 to-pink-600 rounded-full"></div>
                <h2 className="text-2xl font-bold text-slate-900">Life Insurance Details</h2>
              </div>

              {/* Preset and Reset Buttons */}
              <div className="mb-6 space-y-3">
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => handlePreset('conservative')}
                    className="px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg font-semibold text-sm transition-all border border-blue-200"
                  >
                    Conservative
                  </button>
                  <button
                    onClick={() => handlePreset('moderate')}
                    className="px-3 py-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 rounded-lg font-semibold text-sm transition-all border border-indigo-200"
                  >
                    Moderate
                  </button>
                  <button
                    onClick={() => handlePreset('aggressive')}
                    className="px-3 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 rounded-lg font-semibold text-sm transition-all border border-purple-200"
                  >
                    Aggressive
                  </button>
                </div>
                <button
                  onClick={handleReset}
                  className="w-full px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg font-semibold text-sm transition-all border border-slate-300"
                >
                  Reset to Default
                </button>
              </div>

              <div className="space-y-6">
                <InputField
                  label="Your Age"
                  value={lifeInputs.age}
                  onChange={(v) => setLifeInputs({ ...lifeInputs, age: v })}
                  min={18}
                  max={80}
                  unit="years"
                />
                <InputField
                  label="Coverage Amount"
                  value={lifeInputs.coverageAmount}
                  onChange={(v) => setLifeInputs({ ...lifeInputs, coverageAmount: v })}
                  min={50000}
                  step={50000}
                  prefix="$"
                />
                <InputField
                  label="Policy Term"
                  value={lifeInputs.term}
                  onChange={(v) => setLifeInputs({ ...lifeInputs, term: v })}
                  min={10}
                  max={30}
                  step={5}
                  unit="years"
                />
                <SelectField
                  label="Health Status"
                  value={lifeInputs.healthStatus}
                  onChange={(v) => setLifeInputs({ ...lifeInputs, healthStatus: v as any })}
                  options={[
                    { value: 'excellent', label: 'Excellent (No conditions)' },
                    { value: 'good', label: 'Good (Minor conditions)' },
                    { value: 'average', label: 'Average (Some conditions)' },
                    { value: 'poor', label: 'Poor (Major conditions)' },
                  ]}
                />
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 space-y-6">
            <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-rose-600 rounded-3xl p-10 shadow-modern-xl text-white">
              <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-3">
                Estimated Monthly Premium
              </div>
              <div className="text-6xl font-bold mb-2">
                ${lifeResults.monthlyPremium.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-lg opacity-90">Per month</div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <ResultCard
                label="Annual Premium"
                value={`$${lifeResults.annualPremium.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                description="Total yearly cost"
                gradient="from-purple-500 to-pink-600"
              />
              <ResultCard
                label="Total Paid"
                value={`$${lifeResults.totalPaid.toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
                description={`Over ${lifeInputs.term} years`}
                gradient="from-orange-500 to-red-600"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <ResultCard
                label="Coverage Amount"
                value={`$${lifeInputs.coverageAmount.toLocaleString('en-US')}`}
                description="Death benefit"
                gradient="from-blue-500 to-indigo-600"
              />
              <ResultCard
                label="Policy Term"
                value={`${lifeInputs.term} years`}
                description="Coverage period"
                gradient="from-emerald-500 to-teal-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function InputField({
  label,
  value,
  onChange,
  min = 0,
  max,
  step = 1,
  prefix,
  unit,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  prefix?: string;
  unit?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {prefix}
          </span>
        )}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          min={min}
          max={max}
          step={step}
          className={`w-full ${prefix ? 'pl-8' : 'pl-4'} ${unit ? 'pr-16' : 'pr-4'} py-3.5 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all font-semibold text-slate-900 text-lg`}
        />
        {unit && (
          <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-3.5 border-2 border-slate-200 rounded-xl focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 outline-none transition-all font-semibold text-slate-900 text-lg"
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function ResultCard({
  label,
  value,
  description,
  gradient,
}: {
  label: string;
  value: string;
  description: string;
  gradient: string;
}) {
  return (
    <div className={`bg-gradient-to-br ${gradient} rounded-2xl p-6 shadow-modern-lg text-white`}>
      <div className="text-sm font-semibold uppercase tracking-wider opacity-90 mb-2">{label}</div>
      <div className="text-4xl font-bold mb-1">{value}</div>
      <div className="text-sm opacity-90">{description}</div>
    </div>
  );
}
